import { axiosInstance } from '../clients/axiosClient';
import { SYSTEMS_API_PREFIX, API_V1_PREFIX } from '../utils/constants';
import {
  ActiveBulkBodySystemGenerationJobPydantic,
  BodySystemPromptField,
  BodySystemPromptResponse,
  BodySystemResponse,
  CreateBodySystemRequest,
  CreateBodySystemResponse,
  GetBodySystemPromptsResponse,
  BulkBodySystemGenerationJobPydantic,
  ListBodySystemsResponse,
  SaveAndGenerateBodySystemPromptResponsePydantic,
  UpdateBodySystemRequest,
  UpdateBodySystemResponse,
  UpsertBodySystemPromptRequest,
  UpsertBodySystemPromptResponse,
} from '../types/apiGeneratedTypes';

type GetBodySystemsListParams = {
  prompt?: boolean;
};

export const getBodySystemsList = async (
  params?: GetBodySystemsListParams
): Promise<ListBodySystemsResponse> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${SYSTEMS_API_PREFIX}list`,
      {
        params: params?.prompt ? { prompt: true } : undefined,
      }
    );

    return data;
  } catch (error: any) {
    throw new Error(error);
  }
};

export const getBodySystem = async (
  bs_id: string
): Promise<BodySystemResponse> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${SYSTEMS_API_PREFIX}?bs_id=${bs_id}`
    );

    return data;
  } catch (error: any) {
    throw new Error(error);
  }
};

export const createBodySystem = async (
  newBSRequestObject: CreateBodySystemRequest
): Promise<CreateBodySystemResponse> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${SYSTEMS_API_PREFIX}`,
      newBSRequestObject
    );

    return data;
  } catch (error: any) {
    throw new Error(error);
  }
};

export const updateBodySystem = async (
  BSRequestObject: UpdateBodySystemRequest
): Promise<UpdateBodySystemResponse> => {
  try {
    const { data } = await axiosInstance.patch(
      `${API_V1_PREFIX}${SYSTEMS_API_PREFIX}`,
      BSRequestObject
    );

    return data;
  } catch (error: any) {
    throw new Error(error);
  }
};

export const deleteBodySystem = async (id: number): Promise<any> => {
  try {
    await axiosInstance.delete(`${API_V1_PREFIX}${SYSTEMS_API_PREFIX}${id}`);
    return { success: true };
  } catch (error: any) {
    if (error.response?.data?.description) {
      throw new Error(error.response.data.description);
    }
    throw new Error('Failed to delete body system');
  }
};

export const getBodySystemPrompts = async (
  bodySystemId: number
): Promise<GetBodySystemPromptsResponse> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${SYSTEMS_API_PREFIX}${bodySystemId}/prompts`
    );
    return data;
  } catch (error: any) {
    if (error.response?.data?.description) {
      throw new Error(error.response.data.description);
    }
    throw new Error('Failed to load body system prompts');
  }
};

export const getBodySystemPrompt = async (
  bodySystemId: number,
  field: BodySystemPromptField
): Promise<BodySystemPromptResponse> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${SYSTEMS_API_PREFIX}${bodySystemId}/prompts/${field}`
    );
    return data;
  } catch (error: any) {
    if (error.response?.data?.description) {
      throw new Error(error.response.data.description);
    }
    throw new Error(`Failed to load ${field} prompt`);
  }
};

export const upsertBodySystemPrompt = async (
  bodySystemId: number,
  field: BodySystemPromptField,
  payload: UpsertBodySystemPromptRequest
): Promise<UpsertBodySystemPromptResponse> => {
  try {
    const { data } = await axiosInstance.put(
      `${API_V1_PREFIX}${SYSTEMS_API_PREFIX}${bodySystemId}/prompts/${field}`,
      payload
    );
    return data;
  } catch (error: any) {
    if (error.response?.data?.description) {
      throw new Error(error.response.data.description);
    }
    throw new Error(`Failed to save ${field} prompt`);
  }
};

export const saveAndGenerateBodySystemPrompt = async (
  bodySystemId: number,
  field: BodySystemPromptField,
  payload: UpsertBodySystemPromptRequest
): Promise<SaveAndGenerateBodySystemPromptResponsePydantic> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${SYSTEMS_API_PREFIX}${bodySystemId}/prompts/${field}/save-and-generate`,
      payload
    );
    return data;
  } catch (error: any) {
    if (error.response?.data?.description) {
      throw new Error(error.response.data.description);
    }
    throw new Error(`Failed to save ${field} prompt and start generation`);
  }
};

export const revertBodySystemPrompt = async (
  bodySystemId: number,
  field: BodySystemPromptField
): Promise<UpsertBodySystemPromptResponse> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${SYSTEMS_API_PREFIX}${bodySystemId}/prompts/${field}/revert`
    );
    return data;
  } catch (error: any) {
    if (error.response?.data?.description) {
      throw new Error(error.response.data.description);
    }
    throw new Error(`Failed to revert ${field} prompt`);
  }
};

// Running generation job for the body system, or the last one that finished (null if none).
export const getLatestBodySystemGenerationJob = async (
  bodySystemId: number
): Promise<ActiveBulkBodySystemGenerationJobPydantic> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${SYSTEMS_API_PREFIX}${bodySystemId}/generation-job`
    );
    return data;
  } catch (error: any) {
    if (error.response?.data?.description) {
      throw new Error(error.response.data.description);
    }
    throw new Error('Failed to load generation status');
  }
};

export const getBodySystemGenerationJob = async (
  bodySystemId: number,
  jobId: string
): Promise<BulkBodySystemGenerationJobPydantic> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${SYSTEMS_API_PREFIX}${bodySystemId}/generation-jobs/${jobId}`
    );
    return data;
  } catch (error: any) {
    if (error.response?.data?.description) {
      throw new Error(error.response.data.description);
    }
    throw new Error('Failed to load generation status');
  }
};

export const cancelBodySystemGenerationJob = async (
  bodySystemId: number,
  jobId: string
): Promise<BulkBodySystemGenerationJobPydantic> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${SYSTEMS_API_PREFIX}${bodySystemId}/generation-jobs/${jobId}/cancel`
    );
    return data;
  } catch (error: any) {
    if (error.response?.data?.description) {
      throw new Error(error.response.data.description);
    }
    throw new Error('Failed to cancel generation');
  }
};
